import { AmountSuperScript } from "components/design/AmountSuperScript";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { Cross } from "components/icons/Cross";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { RemainingLife } from "pages/Books/FixedAsset/FixedAsset";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetFixedAssetByIdQuery } from "store/apis/fixedAsset";
import { closeSlider, openSlider } from "store/slices/fixedAsset";
import { RootState } from "store/store";
import Tab from "components/DesignSystem/Tab/Tab";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Slider from "components/DesignSystem/Slider/Slider";
import { ScheduleTable } from "./AddAsset";
import { FixedAssetAction } from "pages/Books/FixedAsset/FixedAssetAction";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Form, Formik } from "formik";

const InfoItem = ({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-1">
      <Label className="!t-pb-0">{label}</Label>
      <span className="t-text-subtext">{children}</span>
    </div>
  );
};

const useSilderFixedAsset = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { isSliderOpen, previewFixedAssetId } = useSelector(
    (store: RootState) => store.fixedAsset
  );

  return useGetFixedAssetByIdQuery(
    { entityId, groupId, fixedAssetId: previewFixedAssetId },
    { skip: !groupId || !entityId || !previewFixedAssetId || !isSliderOpen }
  );
};

const Header = () => {
  const dispatch = useDispatch();
  const { data } = useSilderFixedAsset();
  const { asset_name } = data || {};

  const close = () => {
    dispatch(closeSlider());
  };

  return (
    <div className="t-w-full t-px-5 t-py-3 t-border t-border-solid t-border-b t-border-l-0 t-border-t-0 t-border-r-0 t-border-neutral-0 t-bg-surface t-flex t-justify-between t-sticky t-top-0 t-z-header">
      <div className="t-text-subtitle-sm t-break-words t-max-w-32 t-line-clamp-2">
        {asset_name}
      </div>
      <div className="t-flex t-gap-2 t-items-center">
        <FixedAssetAction fixedAsset={data!} />
        <Button customType="ghost_icon" size="xsmall" onClick={close}>
          <Cross color="currentColor" />
        </Button>
      </div>
    </div>
  );
};

const ALL = "ALL";

const SliderBody = () => {
  const { data, isLoading, isSuccess } = useSilderFixedAsset();
  const {
    asset_name,
    description,
    purchase_price = 0,
    salvage_value = 0,
    depreciation_start_date,
    useful_life,
    depreciation_amount = 0,
    asset_account,
    accumulated_depreciation_account,
    depreciation_account,
    accumulated_depreciation_amount = 0,
    current_value = 0,
    frequency,
    remaining_life = 0,
    depreciation_schedule,
  } = data || {};

  const distinctYears = useMemo(() => {
    const years = depreciation_schedule?.map((item) => item.season?.toString());
    return Array.from(new Set(years));
  }, [depreciation_schedule]);

  const seasonOptions = [
    { label: "All", value: ALL },
    ...distinctYears.map((year) => ({
      label: year,
      value: year,
    })),
  ];

  const initialValues = {
    season: ALL,
  };

  return (
    <Async.Root {...{ isEmpty: false, isLoading, isSuccess }}>
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <Tab.Root defaultValue="DETAILS" className="t-h-full">
          <div className="t-flex t-flex-col t-h-full">
            <DashboardContainer className="t-h-full">
              <DashboardContainer.Header>
                <div className="t-sticky t-top-0 t-z-header t-bg-white">
                  <Header />
                  <div className="t-px-5 t-pt-3 t-pb-4">
                    <Tab.List>
                      <Tab.Trigger value="DETAILS">Details</Tab.Trigger>
                      <Tab.Trigger value="DEPRECIATION_SCHEDULE">
                        Depreciation Schedule
                      </Tab.Trigger>
                    </Tab.List>
                  </div>
                </div>
              </DashboardContainer.Header>

              <DashboardContainer.Content>
                <Tab.Content value="DETAILS">
                  <div className="t-p-5 t-flex t-gap-4 t-flex-col t-pb-24">
                    <Accordion.Root
                      type="multiple"
                      defaultValue={["DETAILS", "DEPRECIATION", "CATEGORY"]}
                      className="t-flex t-gap-4 t-flex-col"
                    >
                      <Accordion.Item value="DETAILS">
                        <Accordion.Trigger>Asset Details</Accordion.Trigger>
                        <Accordion.Content>
                          <span className="t-grid t-gap-6 t-grid-cols-2">
                            <InfoItem label="Asset name">
                              <div className="t-break-words t-max-w-40">
                                {asset_name}
                              </div>
                            </InfoItem>
                            <InfoItem label="Description">
                              <div className="t-break-words t-max-w-40">
                                {Boolean(description) ? description : "-"}
                              </div>
                            </InfoItem>
                          </span>
                        </Accordion.Content>
                      </Accordion.Item>

                      <Accordion.Item value="DEPRECIATION">
                        <Accordion.Trigger>Depreciation</Accordion.Trigger>
                        <Accordion.Content>
                          <span className="t-grid t-gap-6 t-grid-cols-2">
                            <InfoItem label="Purchase price">
                              <AmountSuperScript amount={purchase_price} />
                            </InfoItem>

                            <InfoItem label="Salvage value">
                              <AmountSuperScript amount={salvage_value} />
                            </InfoItem>

                            <InfoItem label="Depreciation amount">
                              <AmountSuperScript amount={depreciation_amount} />
                            </InfoItem>

                            <InfoItem label="Current value">
                              <AmountSuperScript amount={current_value} />
                            </InfoItem>

                            <InfoItem label="Depreciation start date">
                              {dayjs(depreciation_start_date).format(
                                DISPLAY_DATE
                              )}
                            </InfoItem>

                            <InfoItem label="Depreciation method">
                              Straight line
                            </InfoItem>

                            <InfoItem label="Useful life (in years)">
                              {useful_life}
                            </InfoItem>

                            <InfoItem label="Remaining life">
                              <RemainingLife
                                frequency={frequency!}
                                remainingLife={remaining_life}
                              />
                            </InfoItem>

                            <InfoItem label="Frequency of depreciation">
                              <div className="first-letter:t-uppercase t-lowercase">
                                {frequency}
                              </div>
                            </InfoItem>
                          </span>
                        </Accordion.Content>
                      </Accordion.Item>

                      <Accordion.Item value="CATEGORY">
                        <Accordion.Trigger>Category</Accordion.Trigger>
                        <Accordion.Content>
                          <span className="t-grid t-gap-6 t-grid-cols-1">
                            <InfoItem label="Asset account">
                              {asset_account?.name}
                            </InfoItem>

                            <InfoItem label="Depreciation expense account">
                              {depreciation_account?.name}
                            </InfoItem>

                            <InfoItem label="Accumulated depreciation account">
                              {accumulated_depreciation_account?.name}
                            </InfoItem>

                            <InfoItem label="Accumulated depreciation amount">
                              <AmountSuperScript
                                amount={accumulated_depreciation_amount}
                              />
                            </InfoItem>
                          </span>
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion.Root>
                  </div>
                </Tab.Content>
                <Tab.Content value="DEPRECIATION_SCHEDULE">
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    onSubmit={() => {}}
                  >
                    {({ values }) => {
                      const selectedSeason = seasonOptions.find(
                        ({ value }) => value === values.season
                      );

                      const filteredData =
                        values.season === ALL
                          ? depreciation_schedule
                          : depreciation_schedule?.filter(
                              (item) =>
                                item.season?.toString() === values.season
                            );

                      return (
                        <Form>
                          <div className="t-px-4 t-pb-4 t-flex t-flex-col t-gap-4">
                            <Combobox
                              withForm
                              menuPortalTarget={document.body}
                              name="season"
                              value={selectedSeason}
                              options={seasonOptions}
                              placeholder="Select Year"
                              size="small"
                              className="t-w-full t-flex t-justify-end"
                              isClearable={false}
                            />
                            <ScheduleTable data={filteredData ?? []} />
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </Tab.Content>
              </DashboardContainer.Content>
            </DashboardContainer>
          </div>
        </Tab.Root>
      </Async.Success>
    </Async.Root>
  );
};

export const AssetSlider = () => {
  const dispatch = useDispatch();

  const { isSliderOpen, previewFixedAssetId } = useSelector(
    (store: RootState) => store.fixedAsset
  );

  const close = () => {
    dispatch(closeSlider());
  };

  return (
    <>
      <Slider.Root
        key={previewFixedAssetId}
        open={isSliderOpen}
        onOpenChange={close}
      >
        <Slider.Content
          open={isSliderOpen}
          useCustomOverlay
          className="t-h-full"
        >
          <SliderBody key={previewFixedAssetId} />
        </Slider.Content>
      </Slider.Root>
    </>
  );
};
